import React, { useEffect, useState } from 'react';
import { Button, Drawer, Form, Input, Modal, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { ADD_RESOURCE } from '../graphql/mutations/addResource';
import { UPDATE_RESOURCE } from '../graphql/mutations/updateResource';
import { DELETE_RESOURCE } from '../graphql/mutations/deleteResource';
import type { ResourceType } from '../graphql/queries/queryResourceByPage';
import { LIST_ALL_RESOURCES } from '../graphql/queries/listAllResources';
import type { ListAllResourcesResponse } from '../graphql/queries/listAllResources';
import type { AddResourceInput } from '../graphql/mutations/addResource';
import type { UpdateResourceInput } from '../graphql/mutations/updateResource';
import { getPlatformPermissionErrorMessage } from '../../utils/errorCode';
import { HasResource } from '../../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

type DrawerMode = 'create' | 'edit';

export interface ResourceDrawerProps {
  visible: boolean;
  mode: DrawerMode;
  onClose: () => void;
  onSuccess: () => void;

  // 顶层新增默认 0；子资源新增默认选中的 parentId
  defaultParentId: number;
  /** 父资源展示名称（不展示 parentId 数字） */
  parentDisplayName: string;
  editRecord: ResourceType | null;
}

const ResourceDrawer: React.FC<ResourceDrawerProps> = ({
  visible,
  mode,
  onClose,
  onSuccess,
  defaultParentId,
  parentDisplayName,
  editRecord,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [addResource, { loading: adding }] = useMutation(ADD_RESOURCE);
  const [updateResource, { loading: updating }] = useMutation(UPDATE_RESOURCE);
  const [deleteResource, { loading: deleting }] = useMutation(DELETE_RESOURCE);

  const typeOptions = [
    { value: 'MENU', label: t('central.platformPermission.resource.typeOptions.menu') as string },
    { value: 'BUTTON', label: t('central.platformPermission.resource.typeOptions.button') as string },
  ];

  const { data: resourceData, loading: resourceLoading } = useQuery<ListAllResourcesResponse>(LIST_ALL_RESOURCES, {
    skip: !visible || mode !== 'edit',
    fetchPolicy: 'network-only',
  });

  const buildHierarchicalLabel = (resource: { id: number | string; name: string; parentId?: number | string | null }, allResources: { id: number | string; name: string; parentId?: number | string | null }[]): string => {
    const idMap = new Map(allResources.map(r => [Number(r.id), r]));
    const parts: string[] = [];
    let current: { id: number | string; name: string; parentId?: number | string | null } | undefined = resource;
    const visited = new Set<number>();
    while (current) {
      const id = Number(current.id);
      if (visited.has(id)) break;
      visited.add(id);
      parts.unshift(current.name);
      current = current.parentId != null && Number(current.parentId) > 0 ? idMap.get(Number(current.parentId)) : undefined;
    }
    return parts.join('-');
  };

  const allResources = resourceData?.listAllResources ?? [];
  const parentOptions = [
    { value: 0, label: t('central.platformPermission.resource.noParent') as string },
    ...allResources
      .filter(r => r.type === 'MENU')
      .map(r => ({ value: Number(r.id), label: buildHierarchicalLabel(r, allResources) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setDeleteModalOpen(false);
      return;
    }

    form.resetFields();

    if (mode === 'edit' && editRecord) {
      form.setFieldsValue({
        id: Number(editRecord.id),
        code: editRecord.code,
        name: editRecord.name,
        type: editRecord.type,
        parentId: editRecord.parentId != null ? Number(editRecord.parentId) : defaultParentId,
        description: editRecord.description ?? null,
      });
    } else {
      form.setFieldsValue({
        id: undefined,
        code: '',
        name: '',
        type: undefined,
        parentId: defaultParentId,
        description: null,
      });
    }
  }, [visible, mode, editRecord, defaultParentId, form]);

  const handleClose = () => {
    form.resetFields();
    setDeleteModalOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (mode === 'edit' && editRecord) {
        const input: UpdateResourceInput = {
          id: Number(values.id),
          code: String(values.code).trim(),
          name: String(values.name).trim(),
          type: values.type,
          parentId: Number(values.parentId),
          description: String(values.description ?? '').trim(),
        };

        const res = await updateResource({
          variables: { input },
        });

        const result = res.data?.updateResource;
        if (result?.success) {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.success(toastText);
          onSuccess();
          onClose();
        } else {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.error(toastText);
        }
      } else {
        const input: AddResourceInput = {
          code: String(values.code).trim(),
          name: String(values.name).trim(),
          type: values.type,
          parentId: Number(values.parentId),
          description: String(values.description ?? '').trim(),
        };

        const res = await addResource({
          variables: { input },
        });

        const result = res.data?.addResource;
        if (result?.success) {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.success(toastText);
          onSuccess();
          onClose();
        } else {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.error(toastText);
        }
      }
    } catch (e: any) {
      if (Array.isArray(e?.errorFields)) return;
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    }
  };

  const handleDelete = () => {
    if (mode !== 'edit' || !editRecord?.id) return;
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (mode !== 'edit' || !editRecord?.id) return;
    try {
      const res = await deleteResource({ variables: { input: { id: Number(editRecord.id) } } });
      const result = res.data?.deleteResource;
      if (result?.success) {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
        if (toastText) message.success(toastText);
        setDeleteModalOpen(false);
        onSuccess();
        onClose();
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
        if (toastText) message.error(toastText);
      }
    } catch (e: any) {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    }
  };

  return (
    <Drawer
      title={
        mode === 'edit'
          ? (t('central.platformPermission.resource.drawer.edit') as string)
          : (t('central.platformPermission.resource.drawer.add') as string)
      }
      open={visible}
      onClose={handleClose}
      destroyOnClose
      width={520}
      footer={
        <div className="flex justify-between gap-2">
          <div>
            {mode === 'edit' && (
                <HasResource resourceCodes={[ResourceCode.resource.DELETE]}>
                  <Button
                      loading={deleting}
                      onClick={handleDelete}
                      className="bg-red-500 border-red-500 text-white hover:!bg-red-500 hover:!border-red-500 hover:!text-white"
                  >
                    {t('components.button.delete') as string}
                  </Button>
                </HasResource>
            )}
          </div>
          <div className="flex gap-2">
            <HasResource resourceCodes={[ResourceCode.resource.ADD, ResourceCode.resource.UPDATE]}>
              <Button
                  type="primary"
                  loading={adding || updating}
                  onClick={handleSubmit}
              >
                {t('components.button.confirm') as string}
              </Button>
            </HasResource>
            <Button onClick={onClose}>
              {t('components.button.cancel') as string}
            </Button>
          </div>
        </div>
      }
    >
      <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} labelAlign="left" labelWrap colon>
        {mode === 'edit' && (
          <Form.Item name="id" label={t('central.platformPermission.resource.fields.id') as string}>
            <Input disabled />
          </Form.Item>
        )}

        {mode === 'edit' ? (
          <Form.Item
            name="parentId"
            label={t('central.platformPermission.resource.parentResource') as string}
            rules={[
              {
                required: true,
                message: t('central.platformPermission.resource.validation.parentRequired') as string,
              },
            ]}
          >
            <Select
              loading={resourceLoading}
              options={parentOptions}
              placeholder={t('components.input.select') as string}
            />
          </Form.Item>
        ) : (
          <>
            {(editRecord?.parentId != null ? Number(editRecord.parentId) : Number(defaultParentId)) > 0 && (
              <Form.Item label={t('central.platformPermission.resource.parentResource') as string}>
                <span className="text-gray-900">{parentDisplayName}</span>
              </Form.Item>
            )}
            <Form.Item name="parentId" hidden>
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="code"
          label={t('central.platformPermission.resource.fields.code') as string}
          rules={[
            {
              required: true,
              message: t('central.platformPermission.resource.validation.codeRequired') as string,
            },
            {
              pattern: /^[A-Za-z0-9_]+$/,
              message: t('central.platformPermission.resource.validation.codePattern') as string,
            },
          ]}
        >
          <Input disabled={mode === 'edit'} placeholder={t('components.input.typeIn') as string} />
        </Form.Item>

        <Form.Item
          name="name"
          label={t('central.platformPermission.resource.fields.name') as string}
          rules={[
            {
              required: true,
              message: t('central.platformPermission.resource.validation.nameRequired') as string,
            },
          ]}
        >
          <Input maxLength={100} placeholder={t('components.input.typeIn') as string} />
        </Form.Item>

        <Form.Item
          name="type"
          label={t('central.platformPermission.resource.fields.type') as string}
          rules={[
            {
              required: true,
              message: t('central.platformPermission.resource.validation.typeRequired') as string,
            },
          ]}
        >
          <Select
            placeholder={t('components.input.select') as string}
            options={typeOptions}
          />
        </Form.Item>

        <Form.Item name="description" label={t('central.platformPermission.resource.fields.description') as string}>
          <Input.TextArea rows={3} maxLength={500} placeholder={t('components.input.typeIn') as string} />
        </Form.Item>
      </Form>
      <Modal
        title={t('central.platformPermission.resource.deleteConfirmTitle') as string}
        open={deleteModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        okText={t('components.button.confirm') as string}
        cancelText={t('components.button.cancel') as string}
        okButtonProps={{
          loading: deleting,
          className: 'bg-purple-600 border-purple-600 hover:!bg-purple-700 hover:!border-purple-700',
        }}
      >
        <p>{t('central.platformPermission.resource.deleteConfirmContent') as string}</p>
      </Modal>
    </Drawer>
  );
};

export default ResourceDrawer;

